import { h, defineComponent, ref, watch } from 'vue';
import { t, d } from '@/ui/i18n/i18n';
import * as qs from '@/ui/queryState';
import { box, indexView, loader, pagination, tag, textColumn } from '@/ui/components';
import { bulkDelete, list as getUserList, remove } from '@/services/api/resources/user';
import { filter as getAllEntityItems } from '@/services/api/resources/entityItem';
import { filter as getAllGroups } from '@/services/api/resources/group';
import { filter as getAllRoles } from '@/services/api/resources/role';
import { emptyList } from '@/services/api';
import { UserList, UserRecord } from '@/entities/user';
import { usePagination, useTask, useSorting } from '@/ui/concerns';
import { dataTitleProp, filterListToOptions, sequence } from '@/ui/helpers/uiHelper';
import { fetchUser, getQuota, hasPermission, hasSubscription, isOnFreePlan, Permissions } from '@/ui/auth/auth';
import { isLastLoginEnabled } from '@/ui/helpers/featureHelper';
import { groupFilter, roleFilter, searchFilter, entityItemFilter } from '@/ui/components/filters/filters-horizontal';
import { tagColumn } from '@/ui/components/table/TableColumn';
import { SelectOptions, SortDirection } from '@/ui/types';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { filterFormLoader } from '@/ui/components/Loader';
import { UpgradeAd } from '@/ui/components/upgrade/UpgradeAd';
import { BulkDeletableTable } from '@/ui/components/table/BulkDeletableTable';
import { bulkDeleteButton } from '@/ui/components/Button';
import { useBulkDeletable } from '@/ui/pages/useBulkDeletable';

export const UserIndexPage = defineComponent({
  name: 'UserIndexPage',
  setup() {
    if (!hasPermission(Permissions.MANAGE_USERS)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);

    // state stored in query string
    const { page, perPage, resetPagination } = usePagination();
    const { sort, dir, onSortChange } = useSorting('createdAt', SortDirection.Descending);
    const search = qs.string('q', '');
    const group = qs.numbers('group', []);
    const entityItem = qs.numbers('entityItem', []);
    const role = qs.numbers('role', []);

    // get filters task
    const entityItems = ref<SelectOptions>([]);
    const roles = ref<SelectOptions>([]);
    const groups = ref<SelectOptions>([]);
    const getFilters = useTask(
      () => sequence(getAllGroups(), getAllEntityItems(), getAllRoles()),
      ([groupList, entityItemList, roleList]) => {
        entityItems.value = filterListToOptions(entityItemList);
        groups.value = filterListToOptions(groupList);
        roles.value = filterListToOptions(roleList);
      },
    );

    // get list task
    const list = ref<UserList>(emptyList());
    const getList = useTask(
      () => getUserList({
        page: page.value,
        perPage: perPage.value,
        search: search.value,
        entityItem: entityItem.value,
        group: group.value,
        role: role.value,
        sort: sort.value,
        dir: dir.value,
      }),
      userList => {
        list.value = userList;
        initialLoading.value = false;
      },
    );
    watch([page, perPage, search, group, entityItem, role, sort, dir], getList.run);

    const formatRole = (user: UserRecord) => {
      if (user.data.admin) {
        return tag('neutral', t('common.administrator'));
      }
      if (user.data.role) {
        return tag('neutral', user.data.role.data.title);
      }
      return '';
    };
    const initialQuota = getQuota('user');
    const userQuota = ref<number>(initialQuota);
    const organizationHasSubscription = hasSubscription();

    const onUserDelete = async () => {
      //actually load new quota
      await fetchUser()();
      userQuota.value = getQuota('user');
    };

    const {
      selectedResources,
      deleteBulkTask,
      onCheckedRowsUpdate,
      isBulkDeletionDisabled,
    } = useBulkDeletable({
      getList,
      bulkDeleteFn: bulkDelete,
      onDeletion: onUserDelete,
    });

    return function render() {
      const upgradeMessage = h(UpgradeAd, {
        class: 'upgrade-ad--user-index',
        props: {
          message: t('upgrade-ad.user-create-quota', { quota: userQuota.value.toString() }),
        },
      });

      return h('div', { class: 'user-index-page' }, [
        organizationHasSubscription && isOnFreePlan.value ? upgradeMessage : null,
        indexView(
          organizationHasSubscription ? [
            filterFormLoader(getFilters, [
              bulkDeleteButton({ resources: selectedResources, disabled: isBulkDeletionDisabled }),
              searchFilter(search, resetPagination),
              entityItemFilter(entityItem, entityItems.value, resetPagination),
              roleFilter(role, roles.value, resetPagination),
              groupFilter(group, groups.value, resetPagination),
            ]),
          ] : [],
          [
            box([
              loader('div', getList,
                [h(BulkDeletableTable, {
                  props: {
                    editRoute: 'userEdit',
                    list: list.value.data,
                    deleteFn: remove,
                    titleColumnLabel: t('common.name'),
                    selectedRecords: selectedResources.value,
                    loading: deleteBulkTask.state.value.pending,
                    paginationLinks: list.value.meta,
                    isRowCheckable: (row: BaseEntity) => row.authorizedTo.delete,
                    recordName: {
                      plural: t('common.users'),
                      singular: t('common.user'),
                    },
                    sort: sort.value,
                    dir: dir.value,
                  },
                  on: {
                    'update:checkedRows': onCheckedRowsUpdate,
                    deleted: onUserDelete,
                    sort: onSortChange,
                  },
                  scopedSlots: {
                    title: ({ row: user }: { row: UserRecord }) => user.data.name,
                    default: () => [
                      textColumn({
                        field: 'email',
                        label: t('common.email'),
                      }, {
                        default: ({ row: user }: { row: UserRecord }) => user.data.email,
                      }),
                      textColumn({
                        field: 'role',
                        label: t('common.role'),
                        width: '250px',
                      }, {
                        default: ({ row: user }: { row: UserRecord }) => [formatRole(user)],
                      }),
                      tagColumn({
                        field: 'groups',
                        label: t('common.groups'),
                      }, {
                        default: ({ row: user }: { row: UserRecord }) =>
                          [user.data.groups.map(dataTitleProp).join(', ')],
                      }),
                      tagColumn({
                        field: 'entityItems',
                        label: t('common.entity-items'),
                      }, {
                        default: ({ row: user }: { row: UserRecord }) =>
                          [user.data.entityItems.map(dataTitleProp).join(', ')],
                      }),
                      tagColumn({
                        field: 'entityFolders',
                        label: t('common.entity-folders'),
                      }, {
                        default: ({ row: user }: { row: UserRecord }) =>
                          [user.data.entityFolders.map(dataTitleProp).join(', ')],
                      }),
                      ...(isLastLoginEnabled() ? [
                        textColumn({
                          field: 'lastLogin',
                          label: t('common.last-login'),
                          width: '150px',
                          sortable: true,
                        }, {
                          default: ({ row: user }: { row: UserRecord }) => {
                            if (user.data.lastLogin) {
                              return [d(new Date(user.data.lastLogin * 1000), 'shortDateTime')];
                            }
                            return ['-'];
                          },
                        }),
                      ] : []),
                    ],
                  },
                }), pagination({
                  current: page.value,
                  total: list.value.meta.total,
                  perPage: perPage.value,
                }),
                ], !initialLoading.value),
            ]),
          ],
        ),
      ]);
    };
  },
});
